import {
  useMutation,
  useMutationState,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "sonner";
import type {
  FigureListResponse,
  FigureReviewAction,
  FigureReviewState,
} from "@patent-studio/api-types";
import { figuresKey } from "@/hooks/query-keys";
import { ApiError } from "@/services/api-client";
import { applyFigureReview } from "@/services/figures";

/** The review writes of one draft, so a screen can tell which figures wait on one. */
function reviewKey(taskId: string) {
  return ["figure-review", taskId] as const;
}

/**
 * The cached review with one answer taken in. An answer is the server's state as it read it
 * before the write, so with several confirms on their way it may predate a neighbour's yes: a
 * confirm takes only its own figure's entry from it, and keeps every other entry the cache holds.
 */
export function withAnswer(
  held: FigureReviewState,
  answer: FigureReviewState,
  action: FigureReviewAction,
): FigureReviewState {
  if (action.op !== "confirm_figure") {
    return { ...answer, figures: { ...answer.figures, ...held.figures } };
  }
  const own = answer.figures?.[action.file_id];
  return own === undefined
    ? held
    : { ...held, figures: { ...held.figures, [action.file_id]: own } };
}

/**
 * Send one review action and keep the cached review standing in step.
 *
 * Confirms on several figures may be on their way at once, each answered in its own time, so
 * each answer is taken in for its own figure alone, and once the last write has landed the
 * review is read again whole. A conflict means the set moved on under the attorney; the cache
 * is refetched so the screen shows what actually stands, rather than the client guessing which
 * yes lapsed.
 */
export function useFigureReview(taskId: string) {
  const qc = useQueryClient();
  const key = useMemo(() => figuresKey(taskId), [taskId]);
  const mutationKey = useMemo(() => reviewKey(taskId), [taskId]);

  return useMutation({
    mutationKey,
    mutationFn: (action: FigureReviewAction) =>
      applyFigureReview(taskId, action),
    onSuccess: (state, action) => {
      qc.setQueryData<FigureListResponse>(key, (old) =>
        old === undefined
          ? old
          : {
              ...old,
              review:
                old.review === undefined
                  ? state
                  : withAnswer(old.review, state, action),
            },
      );
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        toast.info(
          "The figures changed since you looked, so the review has been refreshed.",
        );
        void qc.invalidateQueries({ queryKey: key });
        return;
      }
      toast.error("The confirmation could not be saved. Try again.");
    },
    onSettled: () => {
      // This write still counts while it settles: one means it is the last on its way.
      if (qc.isMutating({ mutationKey }) <= 1) {
        void qc.invalidateQueries({ queryKey: key });
      }
    },
  });
}

/** The figures whose confirm is on its way, so each Confirm waits on its own write alone. */
export function useConfirmsOnTheirWay(taskId: string): Set<string> {
  const ids = useMutationState({
    filters: { mutationKey: reviewKey(taskId), status: "pending" },
    select: (mutation) => {
      const action = mutation.state.variables as FigureReviewAction | undefined;
      return action?.op === "confirm_figure" ? action.file_id : null;
    },
  });
  return new Set(ids.filter((id): id is string => id !== null));
}
